/**
 * Markdown Converter Utility
 *
 * Converts between HTML and Markdown formats while preserving LaTeX math expressions.
 * Supports bidirectional conversion for use with BrainlyMathEditor components.
 *
 * Math expressions format:
 * - Inline: \(E=mc^2\)
 * - Display: \[E=mc^2\]
 *
 * @module markdownConverter
 */

import TurndownService from 'turndown'
import katex from 'katex'
import { marked } from 'marked'

// ============================================================================
// Types
// ============================================================================

interface ConversionOptions {
  /**
   * Preserve newlines in output
   * @default true
   */
  preserveNewlines?: boolean

  /**
   * Use block math ($$) for display mode
   * @default false
   */
  useBlockMath?: boolean
}

// ============================================================================
// HTML to Markdown Conversion
// ============================================================================

/**
 * Convert HTML content to Markdown format
 *
 * Handles:
 * - LaTeX math expressions: <span data-latex="E=mc^2"> → \(E=mc^2\)
 * - Text formatting: <strong> → **, <em> → *
 * - Lists: <ul><li> → - item
 * - Paragraphs and line breaks
 *
 * @param html - HTML string to convert
 * @param options - Conversion options
 * @returns Markdown string
 *
 * @example
 * const html = '<p>Hello <strong>world</strong> <span data-latex="E=mc^2"></span></p>'
 * const markdown = htmlToMarkdown(html)
 * // Returns: "Hello **world** \\(E=mc^2\\)"
 */
export function htmlToMarkdown(
  html: string,
  options: ConversionOptions = {},
): string {
  if (!html || html.trim() === '') {
    return ''
  }

  const { preserveNewlines = true } = options

  // ============================================================================
  // Step 1: Protect existing markdown images from being processed
  // ============================================================================
  const markdownImages: string[] = []
  let protectedHtml = html

  // Extract and protect markdown image syntax: ![alt](url)
  protectedHtml = protectedHtml.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    (match, alt, url) => {
      const index = markdownImages.length
      markdownImages.push(match)
      return `%%%MDIMG${index}%%%`
    },
  )

  // Initialize Turndown with custom rules
  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
    strongDelimiter: '**',
    bulletListMarker: '-',
  })

  // CRITICAL FIX: Prevent TurndownService from escaping markdown characters
  // By default, TurndownService escapes characters like -, *, [, ], etc.
  // This causes double-escaping: - → \- → \\- → \\\- on each save cycle
  // Override the escape function to NOT escape markdown syntax
  turndownService.escape = (text: string) => {
    // Don't escape anything - we want markdown syntax to be preserved
    return text
  }

  // ============================================================================
  // Custom Rule: LaTeX Math Expressions
  // ============================================================================

  /**
   * Convert LaTeX spans to markdown math syntax
   *
   * Handles both old and new formats:
   * - New: <span class="math-content" data-latex="E=mc^2">...</span>
   * - Old: <span class="katex"><span class="katex-mathml">E=mc^2</span>...</span>
   */
  turndownService.addRule('latex-math', {
    filter: (node: HTMLElement) => {
      // New format: span with data-latex attribute
      if (node.nodeName === 'SPAN' && node.hasAttribute('data-latex')) {
        return true
      }

      // Old format: span with katex class containing katex-mathml
      if (node.nodeName === 'SPAN' && node.classList.contains('katex')) {
        return true
      }

      return false
    },
    replacement: (content: string, node: HTMLElement) => {
      // Try to get LaTeX from data-latex attribute (new format)
      let latex = node.getAttribute('data-latex')

      // Fallback: try to get from katex-mathml span (old format)
      if (!latex) {
        const mathmlSpan = node.querySelector('.katex-mathml')
        if (mathmlSpan) {
          latex = mathmlSpan.textContent || ''
        }
      }

      if (!latex || latex.trim() === '') {
        return ''
      }

      // Wrap in \(...\) for inline math
      // Add spaces around math to prevent sticking to adjacent text
      return ` \\(${latex.trim()}\\) `
    },
  })

  // ============================================================================
  // Custom Rule: Extract math-field elements (editing state)
  // ============================================================================

  /**
   * Extract LaTeX from math-field elements
   * Math-field is a temporary editing element that contains LaTeX in its value property
   * We need to extract the LaTeX and convert to markdown math syntax
   */
  turndownService.addRule('math-field', {
    filter: (node: HTMLElement) => {
      return node.nodeName.toLowerCase() === 'math-field'
    },
    replacement: (content: string, node: any) => {
      // Try to get LaTeX from value attribute or textContent
      let latex = ''

      if (node.value) {
        // MathLive math-field has .value property
        latex = node.value
      } else if (node.textContent) {
        // Fallback to textContent
        latex = node.textContent
      } else if (content) {
        // Fallback to content parameter (innerHTML)
        latex = content
      } else if (node.getAttribute && node.getAttribute('value')) {
        // Fallback to value attribute
        latex = node.getAttribute('value')
      }

      if (!latex || latex.trim() === '' || latex.includes('\\placeholder')) {
        // Empty or placeholder - skip
        return ''
      }

      return ` \\(${latex.trim()}\\) `
    },
  })

  // ============================================================================
  // Custom Rule: Convert img tags to markdown image syntax
  // ============================================================================

  /**
   * Convert <img> tags to markdown image syntax: ![alt](src)
   * This ensures clean markdown output that can be rendered by ReactMarkdown
   */
  turndownService.addRule('convert-images-to-markdown', {
    filter: 'img',
    replacement: (content: string, node: HTMLElement) => {
      try {
        const img = node as HTMLImageElement

        // Get src - clean it if malformed
        let src = img.getAttribute('src') || ''

        // Skip if no src
        if (!src) return ''

        // Clean up malformed src URLs
        // Remove trailing ") " or similar artifacts
        src = src.replace(/\)\s*(style|alt|width|height)=.*$/i, '')

        // Remove any trailing ")" that doesn't belong in URLs
        // Keep ")" only if it's part of URL parameters like "?param=(value)"
        if (src.endsWith(')') && !src.includes('=(') && !src.includes('%28')) {
          src = src.slice(0, -1)
        }

        const alt = img.getAttribute('alt') || ''

        // Return markdown image syntax: ![alt](src)
        // Add newlines around image for proper spacing
        return `\n![${alt}](${src})\n`
      } catch (error) {
        // If any error occurs, return empty string to avoid breaking conversion
        console.error(
          '[convert-images-to-markdown] Error processing image:',
          error,
        )
        return ''
      }
    },
  })

  // ============================================================================
  // Custom Rule: Convert HTML lists to markdown (ENABLED for mixed content)
  // ============================================================================

  /**
   * Turndown's default list conversion works well for clean HTML lists.
   * We let it convert <ol> → 1. 2. 3. and <ul> → - - -
   *
   * NOTE: We used to preserve lists as HTML, but that causes issues when
   * content is mixed (markdown images + HTML lists). Now we convert everything
   * to clean markdown for consistency.
   */
  // REMOVED preserve-lists rule - use Turndown's default list conversion

  // ============================================================================
  // Custom Rule: Preserve line breaks
  // ============================================================================

  if (preserveNewlines) {
    turndownService.addRule('line-breaks', {
      filter: 'br',
      replacement: () => '\n',
    })
  }

  // ============================================================================
  // Custom Rule: Convert paragraphs to single newlines
  // ============================================================================

  /**
   * Override default paragraph behavior
   * By default, Turndown converts <p> tags to double newlines (\n\n)
   * We want single newlines to preserve user's formatting
   */
  turndownService.addRule('paragraphs', {
    filter: 'p',
    replacement: (content: string) => {
      return content + '\n'
    },
  })

  // Convert HTML to Markdown (using protected version)
  let markdown = turndownService.turndown(protectedHtml)

  // ============================================================================
  // Step 2: Restore protected markdown images
  // ============================================================================
  markdownImages.forEach((originalImage, index) => {
    const placeholder = `%%%MDIMG${index}%%%`
    markdown = markdown.replace(placeholder, originalImage)
  })

  // Post-processing cleanup
  markdown = cleanupMarkdown(markdown)

  return markdown
}

// ============================================================================
// Markdown to HTML Conversion
// ============================================================================

/**
 * Convert Markdown content to HTML format
 *
 * Handles:
 * - LaTeX math expressions: \(E=mc^2\) → <span data-latex="E=mc^2">...</span>
 * - Text formatting: ** → <strong>, * → <em>
 * - Lists: - item → <ul><li>
 * - Paragraphs
 *
 * @param markdown - Markdown string to convert
 * @param options - Conversion options
 * @returns HTML string with rendered LaTeX
 *
 * @example
 * const markdown = 'Hello **world** \\(E=mc^2\\)'
 * const html = markdownToHtml(markdown)
 * // Returns: '<p>Hello <strong>world</strong> <span data-latex="E=mc^2">...</span></p>'
 */
export function markdownToHtml(
  markdown: string,
  options: ConversionOptions = {},
): string {
  if (!markdown || markdown.trim() === '') {
    return ''
  }

  // Step 0: Unescape first, then check format on clean content
  const unescaped = unescapeMarkdown(markdown)

  // Now detect format on the unescaped content
  const format = detectContentFormat(unescaped)

  let cleanMarkdown = unescaped
  if (format === 'html') {
    // It's HTML or mixed format - convert to clean markdown
    // htmlToMarkdown will preserve the already-unescaped markdown images
    cleanMarkdown = htmlToMarkdown(unescaped, options)
  }

  // Step 1: Extract and protect math expressions
  const { text: protectedText, mathExpressions } =
    extractMathExpressions(cleanMarkdown)

  // Step 2: Convert markdown to HTML (without math)
  let html = convertMarkdownToHtml(protectedText)

  // Step 3: Restore math expressions as HTML spans
  html = restoreMathExpressions(html, mathExpressions)

  return html
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract math expressions from markdown and replace with placeholders
 *
 * @param markdown - Markdown text with math expressions
 * @returns Object with protected text and extracted math expressions
 */
function extractMathExpressions(markdown: string): {
  text: string
  mathExpressions: string[]
} {
  const mathExpressions: string[] = []
  let text = markdown

  // Extract display math first (\[...\])
  text = text.replace(/\\\[(.*?)\\\]/gs, (match, latex) => {
    const index = mathExpressions.length
    mathExpressions.push(latex.trim())
    return `%%%MATHBLOCK${index}%%%`
  })

  // Extract inline math (\(...\))
  text = text.replace(/\\\((.*?)\\\)/g, (match, latex) => {
    const index = mathExpressions.length
    mathExpressions.push(latex.trim())
    return `%%%MATHINLINE${index}%%%`
  })

  return { text, mathExpressions }
}

/**
 * Convert basic markdown syntax to HTML using marked library
 *
 * @param markdown - Markdown text (without math expressions)
 * @returns HTML string
 */
function convertMarkdownToHtml(markdown: string): string {
  try {
    // Configure marked options
    marked.setOptions({
      breaks: true, // Convert \n to <br>
      gfm: true, // GitHub Flavored Markdown
    })

    // Step 1: Protect existing HTML img tags from being modified
    const protectedElements: string[] = []
    let protectedMarkdown = markdown

    // Protect <img> tags that might already be in the content
    protectedMarkdown = protectedMarkdown.replace(/<img[^>]*>/gi, match => {
      try {
        // Clean up malformed img tags from old data
        let cleanedTag = match
        // Fix src URLs with trailing ")" or malformed attributes
        cleanedTag = cleanedTag.replace(
          /src="([^"]*)\)\s*(style|alt|width|height)=/gi,
          (_, url) => `src="${url}"`,
        )
        // Remove trailing ")" from URLs
        cleanedTag = cleanedTag.replace(/src="([^"]*)\)"/gi, (_, url) => {
          if (!url.includes('=(') && !url.includes('%28')) {
            return `src="${url}"`
          }
          return `src="${url})"`
        })
        // Remove duplicate or malformed style attributes
        cleanedTag = cleanedTag.replace(
          /\s+style=["'][^"']*["']\s+style=/gi,
          ' ',
        )

        const index = protectedElements.length
        protectedElements.push(cleanedTag)
        return `%%%PROTECTED${index}%%%`
      } catch (error) {
        console.error('[convertMarkdownToHtml] Error cleaning img tag:', error)
        return match
      }
    })

    // Step 2: Convert markdown to HTML using marked
    let html = marked.parse(protectedMarkdown) as string

    // Step 3: Restore protected elements
    html = html.replace(/%%%PROTECTED(\d+)%%%/g, (match, index) => {
      return protectedElements[parseInt(index, 10)] || ''
    })

    // Step 4: Clean up marked's output
    // Remove wrapping <p> tags if the content is a single paragraph
    html = html.trim()
    if (
      html.startsWith('<p>') &&
      html.endsWith('</p>') &&
      html.split('<p>').length === 2
    ) {
      // Single paragraph - keep the <p> tags for consistency
      // (marked adds them, we should keep them)
    }

    return html
  } catch (error) {
    console.error('[convertMarkdownToHtml] Error converting markdown:', error)
    // Return original markdown if conversion fails
    return markdown
  }
}

/**
 * Restore math expressions as LaTeX HTML spans
 * Uses KaTeX rendering placeholder - actual rendering happens in the editor
 *
 * @param html - HTML with math placeholders
 * @param mathExpressions - Array of LaTeX expressions
 * @returns HTML with LaTeX spans
 */
function restoreMathExpressions(
  html: string,
  mathExpressions: string[],
): string {
  let result = html

  mathExpressions.forEach((latex, index) => {
    // Check if it was a block or inline math
    const blockPlaceholder = `%%%MATHBLOCK${index}%%%`
    const inlinePlaceholder = `%%%MATHINLINE${index}%%%`

    // Create LaTeX span (will be rendered by KaTeX in the editor)
    const latexSpan = createLatexSpan(latex)

    // Replace placeholders (use regex with global flag to replace all occurrences)
    result = result.replace(
      new RegExp(escapeRegex(blockPlaceholder), 'g'),
      latexSpan,
    )
    result = result.replace(
      new RegExp(escapeRegex(inlinePlaceholder), 'g'),
      latexSpan,
    )
  })

  return result
}

/**
 * Escape special regex characters
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Create a LaTeX span element with proper attributes
 * This matches the format used by BrainlyMathEditor
 *
 * @param latex - LaTeX expression
 * @returns HTML span element as string
 */
function createLatexSpan(latex: string): string {
  // Render LaTeX to HTML using KaTeX
  try {
    // Render LaTeX to HTML using KaTeX
    const renderedHtml = katex.renderToString(latex, {
      throwOnError: false,
      displayMode: false,
      strict: false,
      trust: true,
      macros: {
        '\\f': '#1f(#2)',
        '\\RR': '\\mathbb{R}',
        '\\NN': '\\mathbb{N}',
        '\\ZZ': '\\mathbb{Z}',
        '\\QQ': '\\mathbb{Q}',
        '\\CC': '\\mathbb{C}',
      },
    })

    // Inject raw LaTeX into the katex-mathml span for persistence
    const htmlWithLatex = renderedHtml.replace(
      /<span class="katex-mathml">.*?<\/span>/,
      `<span class="katex-mathml">${latex}</span>`,
    )

    // Wrap in math-content span with data-latex attribute
    return `<span class="math-content" data-latex="${escapeHtmlAttribute(latex)}" contenteditable="false">${htmlWithLatex}</span>`
  } catch (error) {
    console.warn('KaTeX rendering failed, using fallback:', error)
    // Fallback: simple span with data-latex attribute
    return `<span class="math-content" data-latex="${escapeHtmlAttribute(latex)}" contenteditable="false">${latex}</span>`
  }
}

/**
 * Escape HTML attribute value
 *
 * @param value - Value to escape
 * @returns Escaped value
 */
function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Clean up markdown output
 * Removes excessive whitespace and normalizes formatting
 *
 * @param markdown - Raw markdown output
 * @returns Cleaned markdown
 */
function cleanupMarkdown(markdown: string): string {
  let cleaned = markdown

  // Remove excessive blank lines (max 2 consecutive)
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n')

  // Normalize SPACES (not newlines!) around math expressions
  // Use [ \t] instead of \s to preserve newlines
  cleaned = cleaned.replace(/[ \t]+\$/g, ' $')
  cleaned = cleaned.replace(/\$[ \t]+/g, '$ ')

  // Remove trailing whitespace from lines
  cleaned = cleaned
    .split('\n')
    .map(line => line.trimEnd())
    .join('\n')

  // Trim start and end
  cleaned = cleaned.trim()

  return cleaned
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Check if content contains LaTeX math expressions
 *
 * @param content - Content to check (HTML or Markdown)
 * @returns True if contains math expressions
 */
export function containsMathExpressions(content: string): boolean {
  // Check for markdown math syntax
  if (/\$.*?\$/.test(content)) {
    return true
  }

  // Check for HTML math spans
  if (
    content.includes('data-latex') ||
    content.includes('class="katex"') ||
    content.includes('class="math-content"')
  ) {
    return true
  }

  return false
}

/**
 * Unescape markdown syntax that may have been escaped during storage
 * Fixes: !\[\]() → ![](), \* → *, etc.
 * Also handles double-escaping: !\\[\\] → ![]
 *
 * @param content - Content with potentially escaped markdown
 * @returns Content with unescaped markdown syntax
 */
export function unescapeMarkdown(content: string): string {
  let result = content

  // First, protect LaTeX delimiters from being unescaped
  const latexPlaceholders: string[] = []

  // Protect \[...\] (display math)
  result = result.replace(/\\\[.*?\\\]/gs, match => {
    const index = latexPlaceholders.length
    latexPlaceholders.push(match)
    return `%%%LATEX_DISPLAY${index}%%%`
  })

  // Protect \(...\) (inline math)
  result = result.replace(/\\\(.*?\\\)/g, match => {
    const index = latexPlaceholders.length
    latexPlaceholders.push(match)
    return `%%%LATEX_INLINE${index}%%%`
  })

  // Handle double-escaped markdown (multiple passes might be needed)
  // Keep unescaping until no more changes occur
  let previousResult = ''
  let iterations = 0
  const maxIterations = 5 // Prevent infinite loops

  while (result !== previousResult && iterations < maxIterations) {
    previousResult = result

    // Unescape compound patterns first (to avoid double-unescaping)
    result = result.replace(/!\\+\[/g, '![') // Unescape image start: !\[ → ![

    // Unescape individual markdown characters
    result = result.replace(/\\+\*/g, '*') // Unescape asterisk: \* → *
    result = result.replace(/\\+_/g, '_') // Unescape underscore: \_ → _
    result = result.replace(/\\+`/g, '`') // Unescape backtick: \` → `
    result = result.replace(/\\+#/g, '#') // Unescape hash: \# → #

    // DO NOT unescape \[ and \] - these are LaTeX display math delimiters
    // DO NOT unescape \( and \) - these are LaTeX inline math delimiters
    // Only unescape single [ ] ( ) that are NOT LaTeX delimiters
    // Keep \[ \] \( \) as-is for LaTeX math

    result = result.replace(/\\+-/g, '-') // Unescape dash: \- → -
    result = result.replace(/\\+\+/g, '+') // Unescape plus: \+ → +
    result = result.replace(/\\+>/g, '>') // Unescape gt: \> → >
    result = result.replace(/\\+!/g, '!') // Unescape exclamation: \! → !
    result = result.replace(/\\+\|/g, '|') // Unescape pipe: \| → |

    iterations++
  }

  // Restore LaTeX delimiters
  latexPlaceholders.forEach((latex, index) => {
    result = result.replace(`%%%LATEX_DISPLAY${index}%%%`, latex)
    result = result.replace(`%%%LATEX_INLINE${index}%%%`, latex)
  })

  return result
}

/**
 * Detect if content is in HTML or Markdown format
 *
 * @param content - Content to check
 * @returns 'html' | 'markdown' | 'unknown'
 */
export function detectContentFormat(
  content: string,
): 'html' | 'markdown' | 'unknown' {
  if (!content || content.trim() === '') {
    return 'unknown'
  }

  // Note: Content should already be unescaped before calling this function
  // Check for markdown math syntax (LaTeX)
  const hasMarkdownMath = /\\\(.*?\\\)|\\\[.*?\\\]/.test(content)

  // Check for HTML tags
  const hasHtmlTags = /<[^>]+>/.test(content)

  // Check for rendered LaTeX spans (proper HTML format)
  const hasLatexSpans =
    /data-latex|class\s*=\s*["'].*?(katex|math-content)/.test(content)

  // Check for markdown image syntax: ![...](...) - MUST be unescaped
  const hasMarkdownImages = /!\[.*?\]\(.*?\)/.test(content)

  // Check for other markdown syntax
  const hasMarkdownSyntax = /\*\*|\*|__|_|\[.*?\]\(.*?\)/.test(content)

  // Check for structured HTML (has paragraph/div wrappers)
  const hasStructuredHtml = /<(p|div|section|article|h[1-6])[^>]*>/.test(
    content,
  )

  // Check if content has HTML images
  const hasHtmlImages = /<img[^>]+>/i.test(content)

  // Check if content has HTML lists
  const hasHtmlLists = /<(ol|ul)[^>]*>/.test(content)

  // Priority detection logic:
  // 1. If has both HTML tags AND markdown images → it's MIXED format, treat as HTML
  //    We need to convert to clean markdown
  //    Example: "![](url) <ol><li>1</li></ol> ![](url2)"
  if (hasMarkdownImages && (hasHtmlTags || hasHtmlLists)) {
    return 'html' // Treat mixed as HTML so we convert everything to clean markdown
  }

  // 2. If has markdown math syntax → treat as markdown
  if (hasMarkdownMath) {
    return 'markdown'
  }

  // 3. If has LaTeX spans (data-latex attribute) → it's properly converted HTML
  if (hasLatexSpans) {
    return 'html'
  }

  // 4. If has markdown images → it's markdown
  if (hasMarkdownImages) {
    return 'markdown'
  }

  // 5. If has HTML lists or images → it's HTML
  if (hasHtmlLists || hasHtmlImages) {
    return 'html'
  }

  // 6. If has structured HTML tags (p, div, etc) → it's HTML
  if (hasStructuredHtml) {
    return 'html'
  }

  // 7. If has other markdown syntax → it's markdown
  if (hasMarkdownSyntax) {
    return 'markdown'
  }

  // 8. If has any HTML tags (fallback) → it's HTML
  if (hasHtmlTags) {
    return 'html'
  }

  return 'unknown'
}

// ============================================================================
// Content Validation Helpers
// ============================================================================

/**
 * Check if HTML content is truly empty (accounting for LaTeX and formatting)
 *
 * This function correctly handles content that contains:
 * - LaTeX math expressions in <span data-latex="...">
 * - Empty HTML tags (<p></p>, <br>, etc.)
 * - Whitespace-only content
 *
 * @param htmlContent - HTML string to check
 * @returns true if content is empty, false if it has meaningful content
 *
 * @example
 * isHtmlContentEmpty('<p></p>') // → true
 * isHtmlContentEmpty('<p>Hello</p>') // → false
 * isHtmlContentEmpty('<span data-latex="E=mc^2"></span>') // → false (has math)
 * isHtmlContentEmpty('<p>  </p>') // → true (only whitespace)
 */
export function isHtmlContentEmpty(
  htmlContent: string | null | undefined,
): boolean {
  // Null/undefined check
  if (!htmlContent) {
    return true
  }

  const trimmed = htmlContent.trim()

  // Empty string
  if (trimmed === '') {
    return true
  }

  // Check if has LaTeX math expressions (data-latex attribute)
  // Use more flexible pattern matching
  if (/data-latex\s*=/i.test(trimmed)) {
    return false
  }

  // Check if has KaTeX rendered content
  if (/class\s*=\s*["'].*?(katex|math-content)/i.test(trimmed)) {
    return false
  }

  // Check if has math-field elements (editing state - not yet converted)
  if (/<math-field/i.test(trimmed)) {
    return false
  }

  // Strip all HTML tags and check remaining text
  const textContent = trimmed
    .replace(/<br\s*\/?>/gi, '') // Remove <br> tags
    .replace(/<[^>]+>/g, '') // Remove all other HTML tags
    .replace(/&nbsp;/gi, ' ') // Convert &nbsp; to space
    .replace(/\u00A0/g, ' ') // Convert non-breaking space to space
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()

  // If no text content remains, it's empty
  return textContent === ''
}
